import { useState } from 'react';
import { Eye, FileText, Pencil, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from '@components/ui/alert-dialog';
import { LIB_LABEL, fmtDate, formatSanctions, stripHtml, type Decision } from '@generated/lib/decision-utils';
import { useSession } from './session';
import { ProtectedViewer } from './ProtectedViewer';
import { SummaryPreviewModal } from './SummaryPreviewModal';
import { ReviewDecisionModal } from './ReviewDecisionModal';

export function DecisionDetailModal({ decision: d, onClose, onUpdated }: { decision: Decision; onClose: () => void; onUpdated: (d: Decision) => void }) {
  const { user, isStaff } = useSession();
  const [agree, setAgree] = useState(false);
  const [viewer, setViewer] = useState(false);
  const [summary, setSummary] = useState(false);
  const [edit, setEdit] = useState(false);
  const canView = isStaff || !!user?.permissions.includes('view_full_decisions');
  const canExport = isStaff || !!user?.permissions.includes('export_summaries');
  const rows: [string, string][] = [
    ['Library', LIB_LABEL[d.lib]], ['Document', d.document], ['Date of Decision', fmtDate(d.date)], ['Adjudicator', d.adjudicator],
    ['Sport', d.sport], ['Process', d.process], ['Level', d.level], ['Province', d.province], ['Jurisdiction', d.jurisdiction.join(', ')],
    ['Complaint Type', d.complaintType.join(', ')], ['Finding', d.finding.join(', ')], ['Sanctions', formatSanctions(d).join('; ')],
    ['Mitigating Factors', d.mitigating.join(', ')], ['Aggravating Factors', d.aggravating.join(', ')], ['Keywords', d.keywords.join(', ')],
    ["Respondent's Role", d.respondentRole.join(', ')], ["Respondent's Age", d.respondentAge], ['Respondent', d.respondentName],
    ['Anonymous Complainant', d.anonymous], ['Severity Warning', d.severityWarning],
  ];
  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent showCloseButton={false} className="flex max-h-[90vh] max-w-3xl flex-col gap-0 overflow-hidden p-0">
          <div className="bg-gradient-to-br from-secondary to-secondary/85 px-6 py-5 text-secondary-foreground">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] opacity-70">Case details</p>
                <DialogTitle className="mt-1 truncate text-2xl font-bold">{d.name}</DialogTitle>
              </div>
              <button onClick={onClose} aria-label="Close" className="rounded-lg p-2 hover:bg-white/10"><X className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {rows.filter(([, v]) => v && v.trim()).map(([k, v]) => (
                <div key={k} className="min-w-0"><dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{k}</dt><dd className="mt-0.5 break-words text-sm text-foreground">{v}</dd></div>
              ))}
            </dl>
            <h3 className="mt-6 border-b border-border pb-1 text-xs font-semibold uppercase tracking-wider text-secondary">Decision summary</h3>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground/85">{stripHtml(d.summary) || 'No summary recorded.'}</p>
            {isStaff && d.notes && (
              <div className="mt-5 rounded-xl border border-primary/15 bg-primary/5 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">Internal notes — staff only</p>
                <p className="mt-1 whitespace-pre-line text-sm">{d.notes}</p>
              </div>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-border bg-muted/60 px-6 py-3">
            {isStaff && <button onClick={() => setEdit(true)} className="flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-4 text-sm font-medium"><Pencil className="h-4 w-4" />Edit</button>}
            {canExport && <button onClick={() => setSummary(true)} className="flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-4 text-sm font-medium"><FileText className="h-4 w-4" />Summary</button>}
            {canView && d.published && <button onClick={() => setAgree(true)} className="flex h-10 items-center gap-1.5 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"><Eye className="h-4 w-4" />View Full Decision</button>}
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={agree} onOpenChange={setAgree}>
        <AlertDialogContent>
          <AlertDialogTitle>Confidentiality Agreement</AlertDialogTitle>
          <AlertDialogDescription>
            This decision is confidential. By continuing you agree to use it only for authorized purposes, not to copy, download, photograph, print or share any part of it, and you acknowledge that your access is recorded and watermarked with your name.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => setViewer(true)}>I agree</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {viewer && <ProtectedViewer lib={d.lib} id={d.id} name={d.name} onClose={() => setViewer(false)} />}
      {summary && <SummaryPreviewModal lib={d.lib} id={d.id} onClose={() => setSummary(false)} />}
      {edit && <ReviewDecisionModal decision={d} onClose={() => setEdit(false)} onSaved={onUpdated} allowPublish={false} />}
    </>
  );
}
